/**
 * Tabla de estado de talonarios del supervisor (presentacional).
 *
 * Replica la tabla/tarjetas del SSR `supervisor_talonarios` (`progress/
 * explore_talonarios_ssr.md`, §2.1/§2.2): por trabajador, rango actual,
 * ocupados/disponibles, % de uso, estado y acciones (ver detalle de órdenes,
 * asignar nuevo rango). Presentacional: recibe los `items` (estado) y un callback
 * para abrir el modal de asignación; el enlace al detalle es un `NavLink`. No hace
 * `fetch` ni decide datos.
 */
import { Link } from "react-router-dom";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";
import { talonarioDetailPath } from "@/features/navigation";

import { usagePercent } from "../format";
import type { TalonarioStatus } from "../types";
import { TalonarioStateBadge } from "./TalonarioStateBadge";

interface TalonariosTableProps {
  items: TalonarioStatus[];
  /** Abre el modal de asignación para un trabajador. */
  onAssign: (worker: string, currentRange: string) => void;
}

export function TalonariosTable({ items, onAssign }: TalonariosTableProps) {
  if (items.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No hay talonarios configurados en esta sede.
      </p>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Trabajador</TableHeaderCell>
          <TableHeaderCell>Rango actual</TableHeaderCell>
          <TableHeaderCell>Ocupados</TableHeaderCell>
          <TableHeaderCell>Disponibles</TableHeaderCell>
          <TableHeaderCell>Uso</TableHeaderCell>
          <TableHeaderCell>Estado</TableHeaderCell>
          <TableHeaderCell>
            <span className="sr-only">Acciones</span>
          </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((talonario) => (
          <TableRow key={talonario.worker}>
            <TableCell className="font-medium text-slate-900">
              {talonario.worker}
            </TableCell>
            <TableCell>{talonario.current_range}</TableCell>
            <TableCell>
              {talonario.occupied} / {talonario.total_numbers}
            </TableCell>
            <TableCell>{talonario.available}</TableCell>
            <TableCell>{usagePercent(talonario.occupied, talonario.total_numbers)}%</TableCell>
            <TableCell>
              <TalonarioStateBadge
                isExhausted={talonario.is_exhausted}
                isLow={talonario.is_low}
              />
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to={talonarioDetailPath(talonario.worker)}>Ver detalle</Link>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onAssign(talonario.worker, talonario.current_range)}
                >
                  Asignar nuevo
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
